import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { thunkActionLoading } from '@configs'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, TableProps, Tooltip } from 'antd'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { applicationManagementAPI } from 'src/api/application-management'
import { Loading } from 'src/common'
import { useDebounce } from 'src/hooks/useDebounce'
import {
  IApplicant,
  IApplicationQuery,
} from 'src/interfaces/application-management'

import { getApplicationAction } from 'src/redux/actions/application-management'

type RangeDate = {
  startDate: string
  endDate: string
}

export const useListCvTable = () => {
  const lastParams = useRef<IApplicationQuery>()
  const dispatch = useAppDispatch()
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteId, setDeleteId] = useState('')
  const [searchString, setSearchString] = useState<string>('')
  const [itemGettingDetail, setItemGettingDetail] = useState<string | null>('')
  const [applicationItem, setApplicationItem] = useState<IApplicant | null>(
    null
  )
  const debouncedValue = useDebounce<string | null>(searchString, 800)
  const applications = useAppSelector((state) => state.application.applications)
  const isGettingApplication = useAppSelector(
    (state) =>
      state.application.loadings[
        thunkActionLoading.GET_APPLICATION_MANAGEMENT_LOADING
      ]
  )

  const [totalApplication, setTotalApplication] = useState<number>()
  const [currentPage, setCurrentPage] = useState<number>()
  const [dateSelect, setDateSelect] = useState<RangeDate | null>()

  const getListApplication = async (params: IApplicationQuery) => {
    if (
      JSON.stringify(params) === JSON.stringify(lastParams.current) &&
      !deleteId
    )
      return
    lastParams.current = params
    try {
      const res = await dispatch(getApplicationAction(params)).unwrap()
      if (res.success) {
        setTotalApplication(res.data.total)
        setCurrentPage(res.data.page)
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const callListJobAfterUpdate = () => {
    if (debouncedValue || dateSelect) {
      handleClear()
    } else {
      getListApplication({ page: 1, limit: 10 })
    }
  }

  const onDeleteApplication = async () => {
    try {
      setIsDeleting(true)
      if (deleteId) {
        const res = await applicationManagementAPI.deleteApplicationPost(
          deleteId
        )
        if (res.success) {
          setDeleteId('')
          callListJobAfterUpdate()
          setIsDeleting(false)
        }
      }
    } catch (error: any) {
      setIsDeleting(true)
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const getDetailApplication = async (id: string) => {
    try {
      setItemGettingDetail(id)
      const res = await applicationManagementAPI.getDetailApplicationId(id)
      if (res.success) {
        setApplicationItem(res.data)
        setItemGettingDetail(null)
      }
    } catch (error: any) {
      setItemGettingDetail(null)
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  useEffect(() => {
    getListApplication({
      search: searchString.trim() || undefined,
      startDate: dateSelect?.startDate ?? undefined,
      endDate: dateSelect?.endDate ?? undefined,
      page: 1,
      limit: 10,
    })
  }, [dateSelect?.startDate])

  useEffect(() => {
    getListApplication({
      search: debouncedValue?.trim() || undefined,
      startDate: dateSelect?.startDate ?? undefined,
      endDate: dateSelect?.endDate ?? undefined,
      page: 1,
      limit: 10,
    })
  }, [debouncedValue])

  const handleClear = () => {
    setSearchString('')
    setDateSelect(undefined)
  }

  const columns: TableProps<IApplicant>['columns'] = [
    {
      dataIndex: 'index',
      align: 'center',
      title: 'No',
      key: 'test',
      width: '2%',
    },
    {
      dataIndex: 'fullName',
      align: 'center',
      key: 'job_title',
      width: '4%',
      title: 'Name',
      render: (_, record) => {
        return (
          <p className="line-clamp-2 whitespace-pre-line">
            {record?.fullName || ''}
          </p>
        )
      },
    },

    {
      dataIndex: 'job_title',
      align: 'center',
      key: 'job_type',
      width: '4%',
      title: 'Job Title',
      render: (_, record) => {
        return (
          <p className="line-clamp-2 whitespace-pre-line">
            {record?.jobPosting?.title || ''}
          </p>
        )
      },
    },
    {
      dataIndex: 'email',
      align: 'center',
      key: 'job_type',
      width: '4%',
      title: 'Email',
      render: (_, record) => {
        return (
          <p className="line-clamp-2 whitespace-pre-line">
            {record?.email || ''}
          </p>
        )
      },
    },

    {
      dataIndex: 'publishedDate',
      align: 'center',
      key: 'date',
      width: '4%',
      title: 'Application Date',
      render: (_, record) => {
        return (
          <p className="line-clamp-2">
            {record?.createdAt
              ? moment(record.createdAt).format('MMMM DD, YYYY')
              : 'N/A'}
          </p>
        )
      },
    },
    {
      dataIndex: 'isViewNextTime',
      align: 'center',
      key: 'job_type',
      width: '4%',
      title: 'User Permission',
      render: (_, record) => {
        return (
          <p className="line-clamp-2">
            {record?.isViewNextTime ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )}
          </p>
        )
      },
    },

    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '4%',
      title: 'Action',
      render: (_, record) => {
        return (
          <div className="flex justify-center w-full space-x-10">
            <Tooltip title={'View detail'}>
              {itemGettingDetail === record.id?.toString() ? (
                <Loading />
              ) : (
                <EyeOutlined
                  className={`text-lg font-light  cursor-pointer  hover:opacity-80 `}
                  onClick={() => {
                    getDetailApplication(record.id?.toString())
                  }}
                />
              )}
            </Tooltip>
            <Tooltip title={'Delete'}>
              <DeleteOutlined
                className={`text-lg font-light  cursor-pointer  hover:opacity-80 !text-red-600`}
                onClick={() => {
                  setDeleteId(record.id?.toString() || '')
                }}
              />
            </Tooltip>
          </div>
        )
      },
      ellipsis: true,
    },
  ]

  return {
    applications,
    columns,
    searchString,
    setSearchString,
    totalApplication,
    getListApplication,
    currentPage,
    isGettingApplication,
    dateSelect,
    setDateSelect,
    handleClear,
    deleteId,
    setDeleteId,
    onDeleteApplication,
    isDeleting,
    callListJobAfterUpdate,
    applicationItem,
    setApplicationItem,
  }
}
